import { formatCourseDateParts, parseCourseCivil } from '@/lib/courseDateFormat';
import { isDocumentInfoSchemaError, normalizeDocumentInfoText } from '@/lib/documentInfo';
import { parseDanishCivilDate } from '@/lib/danishDate';

/**
 * Name shown on a file-marking quote.
 * Dokumentnavn when Dokument info has one, otherwise the file name.
 */
export function documentCitationName(input: {
  documentTitle?: string | null;
  filename?: string | null;
}): string {
  const title = normalizeDocumentInfoText(input.documentTitle ?? '');
  if (title) return title;
  return normalizeDocumentInfoText(input.filename ?? '');
}

/** Af/forfatter from the document. Empty when the file has none. */
export function documentCitationAuthor(documentAuthor: string | null | undefined): string {
  return normalizeDocumentInfoText(documentAuthor ?? '');
}

/** Civil document date (YYYY-MM-DD), or empty when the file has none. */
export function documentCitationCivilDate(documentDate: string | null | undefined): string {
  if (!documentDate) return '';
  return parseDanishCivilDate(documentDate.trim().slice(0, 10)) ?? '';
}

/** Journal-style date without a clock. A document date has no time of day. */
export function formatDocumentCitationDate(
  documentDate: string | null | undefined,
  language: string,
): string {
  const civil = documentCitationCivilDate(documentDate);
  if (!civil) return '';
  const parts = parseCourseCivil(civil);
  if (!parts) return '';
  return formatCourseDateParts(parts, language);
}

/** Drop empty pieces so a missing date or author does not leave a stray separator. */
export function documentCitationAttribution(parts: {
  fileLabel: string;
  pageLabel: string;
  dateLabel: string;
  author: string;
}): string {
  return [parts.fileLabel, parts.pageLabel, parts.dateLabel, parts.author]
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' · ');
}

/**
 * Run the widest read first. A missing Dokument info column (migration not
 * applied yet) retries the next, narrower select. Any other error stops.
 */
export async function firstSchemaCompatible<T>(
  attempts: ReadonlyArray<() => Promise<{ data: T[] | null; error: unknown }>>,
): Promise<T[]> {
  let lastError: unknown;
  for (let index = 0; index < attempts.length; index += 1) {
    const { data, error } = await attempts[index]();
    if (!error) return data ?? [];
    lastError = error;
    if (index < attempts.length - 1 && isDocumentInfoSchemaError(error)) continue;
    throw error;
  }
  throw lastError ?? new Error('document_select_failed');
}
